import { useMemo, useState } from "react";
import { Link, useParams } from "wouter";
import { ArrowLeft, FileText, MessageCircle, Plus, Truck, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Segmented } from "@/components/layout/segmented";
import { estadoDaConsulta } from "@/components/data/estado-consulta";
import { EmptyState } from "@/components/data/empty-state";
import { CompradorShell } from "@/components/mercado/produtor-shell";
import { StatusChip, ChipPagamento } from "@/components/mercado/status-chip";
import { PainelRomaneio } from "@/pages/mercado/comprador/romaneio";
import { useAuth } from "@/lib/auth-context";
import { usePode } from "@/lib/permissoes";
import { useEstadoNaUrl } from "@/lib/estado-na-url";
import { origemAtual, origemDoHistorico, useNavegacao } from "@/lib/navegacao";
import { Tile, Numerao, ChipsDeSituacao, AvatarProdutor } from "@/components/mercado/painel";
import { useCarteiraPainel } from "@/hooks/use-cargas";
import { linkWhatsAppCom } from "@shared/mercado";
import { diaCivilNaFazenda } from "@shared/data-fazenda";
import { useToast } from "@/hooks/use-toast";
import {
  useCarteira, useCargaComRomaneios, useCargasEmFormacao, useIncluirEmCarga, useRomaneios,
} from "@/hooks/use-romaneios";
import { fmtKgBR, fmtMoedaBR, fmtDiaCurto, fmtToneladas, fmtToneladasSemUnidade, fmtDataCurtaBR } from "@/lib/formato-mercado";
import { ORIGENS_ROMANEIOS, PERIODOS_ROMANEIOS, SITUACAO_LABEL, SITUACOES_ROMANEIOS, refDaRota, rotuloDoPeriodo, type FiltroRomaneios, type LinhaRomaneioDTO, type OrigemRomaneio, type PeriodoRomaneios, type SituacaoRomaneio } from "@shared/mercado-romaneios";
import { CAPACIDADE_REFERENCIA_KG, nomeDoProdutor } from "@shared/mercado-inicio";
import { cn } from "@/lib/utils";

/**
 * ROMANEIOS — a caderneta do corretor.
 *
 * Tudo o que ele comprou dos produtores, das duas origens numa lista só: o
 * romaneio que o produtor emitiu no Rizoma e o que ele mesmo anotou no bloco de
 * papel. É a tela em que ele passa o dia.
 *
 * ⚠️ MESMA RÉGUA DA TELA DE VENDAS DO CACHO: números no topo, filtros numa
 * linha, tabela, totais no rodapé. Não é gosto — é a segunda vez que ele vê
 * este layout, e a primeira foi na tela que ele já sabe usar.
 *
 * ⚠️ NENHUMA CONTA AQUI. KPIs, contagens dos chips e totais chegam prontos do
 * servidor; a única aritmética permitida é de LAYOUT (largura de barra em
 * percentual), que é geometria, não dinheiro.
 *
 * ⚠️ O ESTADO DA LISTA MORA NA URL. Não existe `useState` para filtro, busca ou
 * situação: voltar do detalhe devolve a lista como ela estava, e o recorte vira
 * um link que se manda no WhatsApp. O que fica FORA é a UI do momento — a
 * seleção de linhas e o diálogo, que morrem com a tela de propósito.
 */

// ⚠️ Constantes de MÓDULO (são deps de `useMemo` dentro do hook).
/**
 * Os presets de período, na ordem da tela. O filtro é pela DATA DA CARGA (ver
 * `PERIODOS_ROMANEIOS`), nunca pelo vencimento. `tudo` fica fora: é o que
 * "Período…" sem datas já faz.
 */
const OPCOES_PERIODO: { id: PeriodoRomaneios; label: string }[] = [
  { id: "mes", label: "Este mês" },
  { id: "mes-passado", label: "Mês passado" },
  { id: "7d", label: "7 dias" },
  { id: "30d", label: "30 dias" },
  { id: "personalizado", label: "Período…" },
];

const PADRAO = { periodo: "mes", de: "", ate: "", produtor: "todos", origem: "todas", situacao: "todas", q: "" };
const PERMITIDOS = {
  periodo: PERIODOS_ROMANEIOS,
  origem: ORIGENS_ROMANEIOS,
  situacao: SITUACOES_ROMANEIOS,
} as const;

/** Quantas cargas em formação viram botão direto na barra de seleção. */
const MAX_CARGAS_NA_BARRA = 2;

/** Capacidades de caminhão que existem de verdade na praça. */
const CAPACIDADES_KG = [16_000, 18_000, 20_000, 24_000];

export default function CompradorRomaneios() {
  // A mesma tela serve `/mercado/romaneios` e `/mercado/romaneios/:ref`: o
  // detalhe é um painel POR CIMA da lista, e não outra página. Ele confere
  // vários seguidos voltando à lista, e uma navegação de página inteira a cada
  // um custaria o recarregamento dos quatro tiles a cada volta.
  // ⚠️ `refDaRota` e não `params.ref` cru: o wouter entrega o pathname escapado,
  // e o `%3A` seguiria para a API dentro de outro `encodeURIComponent`. Ver a
  // nota em `shared/mercado-romaneios.ts`.
  const { ref: refBruto } = useParams<{ ref?: string }>();
  const ref = refDaRota(refBruto);
  const { user } = useAuth();
  const ehCorretor = user?.orgTipo === "COMPRADOR";
  const podeOperar = usePode("mercado.corretor.operar");

  const { estado, definir, limpar } = useEstadoNaUrl(PADRAO, PERMITIDOS);

  const filtro = useMemo<FiltroRomaneios>(() => ({
    periodo: estado.periodo as PeriodoRomaneios,
    de: estado.de || undefined,
    ate: estado.ate || undefined,
    produtorContatoId: estado.produtor !== "todos" ? estado.produtor : undefined,
    origem: estado.origem !== "todas" ? estado.origem as OrigemRomaneio : undefined,
    situacao: estado.situacao !== "todas" ? estado.situacao as Exclude<SituacaoRomaneio, "todas"> : undefined,
    q: estado.q || undefined,
  }), [estado]);

  const consulta = useRomaneios(filtro, ehCorretor);
  const { data } = consulta;
  const aguardando = estadoDaConsulta(consulta, { variante: "cards" });

  const { data: carteira } = useCarteira(ehCorretor);
  const [selecao, setSelecao] = useState<Set<string>>(new Set());

  const filtrando = estado.periodo !== "mes" || estado.produtor !== "todos"
    || estado.origem !== "todas" || estado.situacao !== "todas" || !!estado.q;

  function alternar(refLinha: string) {
    setSelecao(atual => {
      const proximo = new Set(atual);
      if (proximo.has(refLinha)) proximo.delete(refLinha);
      else proximo.add(refLinha);
      return proximo;
    });
  }

  return (
    <CompradorShell className="space-y-4">
      <VoltarParaProdutores />
      <Cabecalho podeOperar={podeOperar} />

      {/* Com um produtor na URL, a faixa DELE toma o lugar dos quatro tiles:
          "quanto devo a ele, e quando vence" é a pergunta de quem chegou por
          Produtores, e os tiles da carteira inteira responderiam outra. A faixa
          tem o próprio estado de carga e de erro — não some junto com a lista. */}
      {estado.produtor !== "todos" && <FaixaDoProdutor contatoId={estado.produtor} />}

      {aguardando ?? (data && (
        <>
          {estado.produtor === "todos" && (
            <Tiles dados={data} periodo={estado.periodo as PeriodoRomaneios} onVerSemCarga={() => definir({ situacao: "SEM_CARGA", periodo: "tudo", de: "", ate: "" })} />
          )}

          <Filtros
            estado={estado}
            definir={definir}
            carteira={carteira ?? []}
            contagens={data.contagens}
          />

          {data.linhas.length === 0 ? (
            <VazioDaLista filtrando={filtrando} onLimpar={limpar} podeOperar={podeOperar} />
          ) : (
            <>
              <TabelaRomaneios
                linhas={data.linhas}
                totais={data.totais}
                selecao={selecao}
                onAlternar={podeOperar ? alternar : undefined}
                refAberta={ref}
              />
              {podeOperar && selecao.size > 0 && (
                <BarraSelecao
                  linhas={data.linhas.filter(l => selecao.has(l.ref))}
                  onLimpar={() => setSelecao(new Set())}
                />
              )}
            </>
          )}
        </>
      ))}

      {/* O painel fica FORA do `aguardando`: abrir um romaneio por link direto
          não pode depender de a lista já ter carregado. */}
      {ref && <PainelRomaneio refRomaneio={ref} />}
    </CompradorShell>
  );
}

// ─── Cabeçalho ───────────────────────────────────────────────────────────────

function Cabecalho({ podeOperar }: { podeOperar: boolean }) {
  return (
    <header className="flex flex-wrap items-end justify-between gap-3">
      <div className="min-w-0">
        <h1 className="text-2xl font-bold tracking-tight" data-testid="titulo-romaneios">Romaneios</h1>
        <p className="text-sm text-muted-foreground mt-0.5 max-w-[60ch]">
          Tudo o que você comprou dos produtores: recebido pelo Rizoma ou de registro próprio.
        </p>
      </div>
      <div className="flex gap-2">
        <Button variant="outline" asChild>
          <Link href="/mercado/cargas" state={origemAtual()}><Truck className="h-4 w-4 mr-1.5" />Cargas</Link>
        </Button>
        {/* Quem só tem `ver` abre a tela inteira e não vê botão de ação. O gate
            de verdade é `requireCap` no servidor; esconder evita oferecer uma
            tela que responderia 403. */}
        {podeOperar && (
          <Button asChild data-testid="button-novo-romaneio">
            <Link href="/mercado/romaneios/novo" state={origemAtual()}>
              <Plus className="h-4 w-4 mr-1.5" />Novo romaneio
            </Link>
          </Button>
        )}
      </div>
    </header>
  );
}

// ─── Os quatro números ───────────────────────────────────────────────────────

function Tiles({
  dados, periodo, onVerSemCarga,
}: {
  dados: { kpis: import("@shared/mercado-romaneios").KpisRomaneios };
  periodo: PeriodoRomaneios;
  onVerSemCarga: () => void;
}) {
  const { romaneios, volume, valor, pendentes } = dados.kpis;
  const hoje = new Date().toISOString().slice(0, 10);
  // `pendentes.n` e não a soma: o romaneio a conferir E sem carga é UM romaneio.
  const totalPendente = pendentes.n;
  // Geometria, não dinheiro: a barra de mistura é a fração do maior somatório.
  const totalVariedades = volume.porVariedade.reduce((a, v) => a + v.kg, 0);

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="tiles-romaneios">
      <Tile rotulo={`Romaneios · ${rotuloDoPeriodo(periodo, hoje)}`}>
        <Numerao
          valor={String(romaneios.n)}
          unidade={romaneios.n === 1 ? "romaneio" : "romaneios"}
          testId="tile-romaneios"
        />
        <p className="text-xs text-muted-foreground">
          {romaneios.viaRizoma} pelo Rizoma · {romaneios.registroProprio} de registro próprio
        </p>
      </Tile>

      <Tile rotulo="Volume comprado">
        <Numerao valor={fmtToneladasSemUnidade(volume.kg)} unidade="toneladas" testId="tile-volume" />
        {totalVariedades > 0 && (
          <div className="h-2 rounded-full bg-muted overflow-hidden flex mt-auto" aria-hidden>
            {volume.porVariedade.map((v, i) => (
              <div
                key={v.variedade}
                style={{
                  width: `${(v.kg / totalVariedades) * 100}%`,
                  background: `hsl(var(--chart-${(i % 5) + 1}))`,
                }}
              />
            ))}
          </div>
        )}
        <p className="text-xs text-muted-foreground">
          {volume.porVariedade.length > 0
            ? volume.porVariedade.map(v => `${cap(v.variedade)} ${fmtToneladas(v.kg)}`).join(" · ")
            : "nenhum romaneio no período"}
        </p>
      </Tile>

      <Tile rotulo="Valor comprado">
        <Numerao valor={fmtMoedaBR(valor.total)} testId="tile-valor" />
        <p className="text-xs text-muted-foreground">
          {/* `null` e não "R$ 0,00/kg": sem volume não há média praticada. */}
          {valor.mediaKg != null ? <>média {fmtMoedaBR(valor.mediaKg)}/kg</> : "sem volume no período"}
        </p>
      </Tile>

      {/* ⚠️ PENDENTES IGNORA O PERÍODO — pendência antiga continua pendência, e
          somê-la do contador seria esconder o que já foi esquecido uma vez. O
          rótulo diz isso, e "Ver os sem carga" abre a lista em "tudo": com o
          período do mês, o card dizia 12 e a lista mostrava 5. */}
      <Tile rotulo="Pendentes · todo o período" alerta={totalPendente > 0}>
        <Numerao valor={String(totalPendente)} unidade={totalPendente === 1 ? "romaneio" : "romaneios"} testId="tile-pendentes" />
        <p className="text-xs text-muted-foreground">
          {pendentes.aConferir} a conferir · {pendentes.semCarga.n} sem carga
          {pendentes.semCarga.kg > 0 && <> ({fmtToneladas(pendentes.semCarga.kg)})</>}
        </p>
        {pendentes.semCarga.n > 0 && (
          <button
            type="button"
            onClick={onVerSemCarga}
            className="mt-auto text-xs font-semibold text-primary text-left"
            data-testid="link-ver-sem-carga"
          >
            Ver os sem carga →
          </button>
        )}
      </Tile>
    </div>
  );
}

// ─── Aberto por produtor (Produtores v2) ─────────────────────────────────────

/**
 * "← Produtores" — só quando a origem foi Produtores. Quem chegou pelo menu não
 * veio de lá, e um breadcrumb para uma tela que ele não visitou é ruído.
 * `voltar` usa o histórico quando a origem é nossa: devolve Produtores com os
 * filtros que estavam na URL.
 */
function VoltarParaProdutores() {
  const { voltar } = useNavegacao();
  const origem = origemDoHistorico(typeof window !== "undefined" ? window.history.state : null);
  if (!origem || !origem.startsWith("/mercado/produtores")) return null;
  return (
    <button
      type="button"
      onClick={() => voltar("/mercado/produtores")}
      className="inline-flex items-center gap-1 text-sm font-semibold text-primary"
      data-testid="voltar-produtores"
    >
      <ArrowLeft className="h-4 w-4" />Produtores
    </button>
  );
}

/**
 * O resumo de UM produtor, no topo de Romaneios.
 *
 * ⚠️ OS MESMOS NÚMEROS DE PRODUTORES — o painel da carteira com `produtor=`, e
 * não uma segunda leitura de dívida. Em aberto e vencido ignoram o período (é
 * dívida); "Comprado" é sempre 30 dias, que é o que o rótulo diz.
 */
function FaixaDoProdutor({ contatoId }: { contatoId: string }) {
  const consulta = useCarteiraPainel({ periodo: "30d", contatoId });
  const aguardando = estadoDaConsulta(consulta, { variante: "painel" });
  if (aguardando) return aguardando;
  const p = consulta.data?.linhas[0];
  if (!p) return null;
  // O dia civil DA FAZENDA, e não o UTC: às 22h de Ibipeba o UTC já é amanhã, e
  // "em 2 d" viraria "em 1 d" a noite inteira.
  const hoje = diaCivilNaFazenda(new Date());

  return (
    <section
      className="rounded-lg border border-l-4 border-l-primary bg-card p-4 flex flex-col gap-3 lg:flex-row lg:items-center"
      data-testid="faixa-produtor"
    >
      <div className="flex items-center gap-3 min-w-0 lg:w-72">
        <AvatarProdutor nome={p.nome} noRizoma={p.noRizoma} />
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold truncate">{p.nome}</span>
            {p.noRizoma ? <StatusChip kind="pago">Rizoma</StatusChip> : <StatusChip kind="aberta">Registro próprio</StatusChip>}
          </div>
          <p className="text-xs text-muted-foreground truncate">
            {[p.fazenda && p.fazenda !== p.nome ? p.fazenda : null, p.cidade].filter(Boolean).join(" · ") || "—"}
          </p>
        </div>
      </div>

      <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 flex-1">
        <NumeroDaFaixa rotulo="Em aberto" valor={fmtMoedaBR(p.emAberto)} testId="faixa-em-aberto" />
        <NumeroDaFaixa
          rotulo="Vencido"
          valor={fmtMoedaBR(p.vencido)}
          perigo={p.vencido > 0}
          testId="faixa-vencido"
        />
        <NumeroDaFaixa
          rotulo="Próximo vencimento"
          valor={p.proximoVencimento ? fmtDataCurtaBR(p.proximoVencimento) : "—"}
          sub={p.proximoVencimento ? emDias(p.proximoVencimento, hoje) : undefined}
        />
        <NumeroDaFaixa rotulo="Comprado · 30 dias" valor={fmtToneladas(p.periodo.kg)} />
      </dl>

      <div className="flex gap-2 lg:self-end">
        {p.telefone && (
          <Button variant="outline" size="sm" asChild>
            <a href={linkWhatsAppCom(p.telefone, "")} target="_blank" rel="noreferrer" data-testid="faixa-whatsapp">
              <MessageCircle className="h-4 w-4 mr-1.5" />WhatsApp
            </a>
          </Button>
        )}
        <Button variant="outline" size="sm" asChild>
          <Link href={`/mercado/pagar?produtor=${p.contatoId}`} state={origemAtual()} data-testid="faixa-ver-a-pagar">
            Ver no A pagar
          </Link>
        </Button>
      </div>
    </section>
  );
}

function NumeroDaFaixa({
  rotulo, valor, sub, perigo, testId,
}: { rotulo: string; valor: string; sub?: string; perigo?: boolean; testId?: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">{rotulo}</dt>
      <dd className={cn("font-mono tabular-nums font-semibold", perigo && "text-destructive")} data-testid={testId}>{valor}</dd>
      {sub && <dd className="text-[11px] text-muted-foreground">{sub}</dd>}
    </div>
  );
}

/** "em 2 d", "hoje", "há 3 d" — a distância de um vencimento. */
function emDias(data: string, hoje: string): string {
  const n = Math.round((Date.parse(`${data}T12:00:00Z`) - Date.parse(`${hoje}T12:00:00Z`)) / 86_400_000);
  return n === 0 ? "hoje" : n > 0 ? `em ${n} d` : `há ${-n} d`;
}

// ─── Filtros ─────────────────────────────────────────────────────────────────

function Filtros({
  estado, definir, carteira, contagens,
}: {
  estado: typeof PADRAO;
  definir: (p: Partial<typeof PADRAO>) => void;
  carteira: { id: string; nome: string }[];
  contagens: Record<SituacaoRomaneio, number>;
}) {
  const personalizado = estado.periodo === "personalizado";
  // O nome da pílula sai do CADASTRO, que a tela já carrega para o select —
  // sem uma consulta a mais. Enquanto ele não chega, "…" e não o UUID.
  const nomeDoProdutor = carteira.find(p => p.id === estado.produtor)?.nome ?? "…";
  // Trocar de preset limpa as datas; "Período…" as mantém para serem digitadas.
  const mudarPeriodo = (v: PeriodoRomaneios) =>
    definir(v === "personalizado" ? { periodo: v } : { periodo: v, de: "", ate: "" });

  return (
    <div className="space-y-2.5">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        {/* ⚠️ DOIS CONTROLES, UMA LISTA DE OPÇÕES. Com "Mês passado" são cinco
            botões, e medidos no Chromium a 390 px eles somam ~418 px contra 358
            de largura útil — o segmentado (`whitespace-nowrap`) estoura a tela.
            No celular vira Select, igual aos de produtor e origem logo abaixo;
            do `sm` para cima continua o segmentado, que é um toque só. */}
        <Select value={estado.periodo} onValueChange={v => mudarPeriodo(v as PeriodoRomaneios)}>
          <SelectTrigger className="sm:hidden" data-testid="filtro-periodo-romaneios">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {OPCOES_PERIODO.map(o => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Segmented<PeriodoRomaneios>
          valor={estado.periodo as PeriodoRomaneios}
          onChange={mudarPeriodo}
          testId="seg-periodo-romaneios"
          className="hidden sm:inline-flex"
          opcoes={OPCOES_PERIODO}
        />

        {/* ⚠️ COM PRODUTOR NA URL, PÍLULA — e não o select. O select escondido
            no meio dos filtros não deixava claro que a lista estava recortada,
            e quem chegava de Produtores achava que eram todos os romaneios.
            Tirar a pílula volta a todos os produtores, mantendo o período. */}
        {estado.produtor !== "todos" ? (
          <button
            type="button"
            onClick={() => definir({ produtor: "todos" })}
            className="inline-flex items-center gap-1.5 self-start whitespace-nowrap rounded-full border border-primary bg-primary/10 px-3 py-1.5 text-sm font-semibold text-primary hover-elevate"
            data-testid="pilula-produtor"
            aria-label={`Remover o filtro de produtor ${nomeDoProdutor}`}
          >
            Produtor: {nomeDoProdutor}
            <X className="h-3.5 w-3.5" />
          </button>
        ) : (
        <Select value={estado.produtor} onValueChange={v => definir({ produtor: v })}>
          <SelectTrigger className="sm:w-52" data-testid="filtro-produtor">
            <SelectValue placeholder="Todos os produtores" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos os produtores</SelectItem>
            {carteira.map(p => (
              <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        )}

        <Select value={estado.origem} onValueChange={v => definir({ origem: v })}>
          <SelectTrigger className="sm:w-52" data-testid="filtro-origem">
            <SelectValue placeholder="Todas as origens" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todas">Todas as origens</SelectItem>
            <SelectItem value="RIZOMA">Recebidos pelo Rizoma</SelectItem>
            <SelectItem value="REGISTRO_PROPRIO">Registro próprio</SelectItem>
          </SelectContent>
        </Select>

        <Input
          value={estado.q}
          onChange={e => definir({ q: e.target.value })}
          placeholder="Buscar produtor ou nº"
          className="sm:ml-auto sm:w-56"
          data-testid="busca-romaneios"
          aria-label="Buscar por produtor ou número"
        />
      </div>

      {personalizado && (
        <div className="flex gap-2">
          <Input type="date" value={estado.de} onChange={e => definir({ de: e.target.value })} aria-label="De" />
          <Input type="date" value={estado.ate} onChange={e => definir({ ate: e.target.value })} aria-label="Até" />
        </div>
      )}

      <ChipsDeSituacao
        opcoes={SITUACOES_ROMANEIOS.map(s => ({ id: s, label: SITUACAO_LABEL[s], n: contagens[s] }))}
        valor={estado.situacao as SituacaoRomaneio}
        onChange={s => definir({ situacao: s })}
        testId="chips-situacao"
      />
    </div>
  );
}

// ─── Vazio ───────────────────────────────────────────────────────────────────

function VazioDaLista({
  filtrando, onLimpar, podeOperar,
}: { filtrando: boolean; onLimpar: () => void; podeOperar: boolean }) {
  // Vazio COM filtro e vazio TOTAL são situações diferentes: a primeira pede
  // para afrouxar o recorte, a segunda convida a registrar o primeiro romaneio.
  // Um texto só para as duas erraria metade das vezes.
  if (filtrando) {
    return (
      <EmptyState
        icone={FileText}
        titulo="Nenhum romaneio com esses filtros."
        descricao="Tente ampliar o período ou limpar o recorte."
        acao={<Button variant="outline" onClick={onLimpar} data-testid="button-limpar-filtros">Limpar filtros</Button>}
      />
    );
  }
  return (
    <EmptyState
      icone={FileText}
      titulo="Nenhum romaneio ainda"
      descricao="Os produtores do Rizoma mandam o romaneio pelo sistema. Os demais, você registra aqui — leva um minuto."
      acao={podeOperar
        ? (
          <Button asChild data-testid="button-registrar-primeiro">
            <Link href="/mercado/romaneios/novo" state={origemAtual()}>
              <Plus className="h-4 w-4 mr-1.5" />Registrar romaneio
            </Link>
          </Button>
        )
        : undefined}
    />
  );
}

// ─── Tabela (desktop) e cartões (celular) ────────────────────────────────────

function ChipOrigem({ origem }: { origem: OrigemRomaneio }) {
  return origem === "RIZOMA"
    ? <StatusChip kind="pago" testId="chip-origem">Rizoma</StatusChip>
    : <StatusChip kind="aberta" testId="chip-origem">Registro próprio</StatusChip>;
}

/**
 * "Hugo (Fazenda Boa Vista)" no romaneio do Rizoma — a pessoa e a fazenda
 * (Hugo, 24/09). No registro próprio o segundo campo é a CIDADE do contato, e
 * "Zé (Irecê)" leria como se Irecê fosse a fazenda: lá fica só o nome.
 */
function quemEntregou(l: LinhaRomaneioDTO): string {
  return l.origem === "RIZOMA" ? nomeDoProdutor(l.produtor.nome, l.produtor.fazenda) : l.produtor.nome;
}

function ChipConferencia({ estado }: { estado: LinhaRomaneioDTO["conferencia"] }) {
  // `null` é registro próprio: não há conferência a fazer sobre o que ele mesmo
  // anotou, e um chip ali afirmaria um ato que ninguém praticou.
  if (estado == null) return <span className="text-muted-foreground text-xs">—</span>;
  if (estado === "A_CONFERIR") return <StatusChip kind="warn">A conferir</StatusChip>;
  if (estado === "AJUSTADO") return <StatusChip kind="conf">Ajustado</StatusChip>;
  return <StatusChip kind="conf">Conferido</StatusChip>;
}

function ChipCargaDaLinha({ carga }: { carga: LinhaRomaneioDTO["carga"] }) {
  if (!carga) {
    return (
      <span className="inline-flex items-center rounded-full border border-dashed px-2 py-0.5 text-[11px] text-muted-foreground">
        Sem carga
      </span>
    );
  }
  const kind = carga.status === "ENTREGUE" ? "pago" : carga.status === "FECHADA" ? "vista" : "warn";
  return (
    <Link
      href={`/mercado/cargas/${carga.id}`}
      state={origemAtual()}
      onClick={e => e.stopPropagation()}
      data-testid={`chip-carga-${carga.identificador}`}
    >
      <StatusChip kind={kind}>{carga.identificador}</StatusChip>
    </Link>
  );
}

/**
 * O vencimento com a urgência embutida — vermelho vencido, âmbar em até 3 dias.
 *
 * A conta de dias é de LAYOUT (que cor usar), não de dinheiro: a data em si vem
 * do servidor, e é ela que vale.
 */
function Vencimento({ linha }: { linha: LinhaRomaneioDTO }) {
  if (!linha.vencimento) return <StatusChip kind="warn">Sem vencimento</StatusChip>;
  if (linha.pagamento === "PAGO") {
    return <span className="text-muted-foreground tabular-nums">{fmtDiaCurto(linha.vencimento)}</span>;
  }
  const dias = Math.round(
    (new Date(`${linha.vencimento}T00:00:00Z`).getTime() - new Date(`${new Date().toISOString().slice(0, 10)}T00:00:00Z`).getTime())
    / 86_400_000,
  );
  const tom = dias < 0 ? "text-destructive" : dias <= 3 ? "text-[hsl(var(--warning,38_92%_40%))]" : "";
  return (
    <span className="tabular-nums">
      <b className={tom}>{fmtDiaCurto(linha.vencimento)}</b>
      <span className="block text-[11px] text-muted-foreground">
        {dias < 0 ? "vencido" : dias === 0 ? "vence hoje" : `em ${dias} ${dias === 1 ? "dia" : "dias"}`}
      </span>
    </span>
  );
}

/** A barra de classificação — largura proporcional, cor por posição. */
function BarraClassificacao({ linha }: { linha: LinhaRomaneioDTO }) {
  const total = linha.itens.reduce((a, i) => a + i.kg, 0);
  if (total <= 0) return null;
  return (
    <div className="space-y-1 min-w-[120px]">
      <div className="h-1.5 rounded-full bg-muted overflow-hidden flex" aria-hidden>
        {linha.itens.map((i, idx) => (
          <div
            key={`${i.classificacao}-${idx}`}
            style={{ width: `${(i.kg / total) * 100}%`, background: `hsl(var(--chart-${(idx % 5) + 1}))` }}
          />
        ))}
      </div>
      <p className="text-[11px] text-muted-foreground truncate">
        {linha.itens.map(i => `${i.classificacao} ${Math.round((i.kg / total) * 100)}%`).join(" · ")}
      </p>
    </div>
  );
}

function TabelaRomaneios({
  linhas, totais, selecao, onAlternar, refAberta,
}: {
  linhas: LinhaRomaneioDTO[];
  totais: { n: number; nProdutores: number; kg: number; valor: number };
  selecao: Set<string>;
  /** `undefined` para quem só tem `ver` — sem caixa de seleção, sem barra. */
  onAlternar?: (ref: string) => void;
  refAberta?: string;
}) {
  const { abrir } = useNavegacao();

  return (
    <>
      {/* ─── Celular: cartões ──────────────────────────────────────────────
          A tabela de dez colunas não cabe em 390 px, e rolagem lateral numa
          lista que ele percorre com o polegar é o caminho mais curto para
          desistir da tela. A coluna de classificação sai: é a informação de
          conferência, e no celular ele está atrás de produtor, kg e valor. */}
      <ul className="md:hidden space-y-2" data-testid="cartoes-romaneios">
        {linhas.map(l => (
          <li key={l.ref}>
            <div
              role="button"
              tabIndex={0}
              onClick={() => abrir(`/mercado/romaneios/${encodeURIComponent(l.ref)}`)}
              onKeyDown={e => { if (e.key === "Enter") abrir(`/mercado/romaneios/${encodeURIComponent(l.ref)}`); }}
              className={cn(
                "rounded-lg border bg-card p-3 space-y-2 hover-elevate cursor-pointer",
                refAberta === l.ref && "border-primary",
              )}
              data-testid={`cartao-romaneio-${l.ref}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold truncate" title={quemEntregou(l)}>{quemEntregou(l)}</p>
                  <p className="text-xs text-muted-foreground font-mono truncate">
                    {l.origem === "RIZOMA" ? l.numero : `nº ${l.numero}`} · {fmtDiaCurto(l.data)}
                  </p>
                </div>
                <div className="shrink-0 text-right">
                  <p className="font-semibold tabular-nums">{fmtMoedaBR(l.valor)}</p>
                  <p className="text-xs text-muted-foreground tabular-nums">{fmtKgBR(l.kgBase)}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                <ChipOrigem origem={l.origem} />
                {l.conferencia && <ChipConferencia estado={l.conferencia} />}
                <ChipCargaDaLinha carga={l.carga} />
                <ChipPagamento status={l.pagamento} />
                {!l.vencimento && <StatusChip kind="warn">Sem vencimento</StatusChip>}
              </div>
              {onAlternar && l.carga == null && (
                <label className="flex items-center gap-2 text-xs text-muted-foreground pt-1" onClick={e => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selecao.has(l.ref)}
                    onChange={() => onAlternar(l.ref)}
                    className="h-4 w-4"
                    data-testid={`check-${l.ref}`}
                  />
                  Selecionar para montar carga
                </label>
              )}
            </div>
          </li>
        ))}
      </ul>

      {/* ─── Desktop: tabela ───────────────────────────────────────────────── */}
      <div className="hidden md:block rounded-lg border overflow-hidden">
        <table className="w-full text-sm" data-testid="tabela-romaneios">
          <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="w-9 p-2" />
              <th className="text-left font-medium p-2">Romaneio</th>
              <th className="text-left font-medium p-2">Produtor</th>
              <th className="text-left font-medium p-2">Classificação</th>
              <th className="text-right font-medium p-2">kg</th>
              <th className="text-right font-medium p-2">Valor</th>
              <th className="text-left font-medium p-2">Vencimento</th>
              <th className="text-left font-medium p-2">Conferência</th>
              <th className="text-left font-medium p-2">Carga</th>
              <th className="text-left font-medium p-2">Pagamento</th>
            </tr>
          </thead>
          <tbody>
            {linhas.map(l => (
              <tr
                key={l.ref}
                onClick={() => abrir(`/mercado/romaneios/${encodeURIComponent(l.ref)}`)}
                className={cn(
                  "border-t cursor-pointer hover-elevate",
                  refAberta === l.ref && "bg-primary/5",
                  selecao.has(l.ref) && "bg-muted/40",
                )}
                data-testid={`linha-romaneio-${l.ref}`}
              >
                <td className="p-2 align-middle" onClick={e => e.stopPropagation()}>
                  {/* ⚠️ SÓ romaneio SEM CARGA tem caixa: incluir na carga é o que
                      a seleção faz, e oferecê-la em quem já está num caminhão
                      seria prometer uma ação que o servidor recusa. */}
                  {onAlternar && l.carga == null && (
                    <input
                      type="checkbox"
                      checked={selecao.has(l.ref)}
                      onChange={() => onAlternar(l.ref)}
                      className="h-4 w-4"
                      aria-label={`Selecionar romaneio ${l.numero}`}
                      data-testid={`check-${l.ref}`}
                    />
                  )}
                </td>
                <td className="p-2 align-middle">
                  <b className="font-mono">{l.origem === "RIZOMA" ? l.numero : `nº ${l.numero}`}</b>
                  <span className="block text-[11px] text-muted-foreground">
                    {fmtDiaCurto(l.data)}{l.origem === "REGISTRO_PROPRIO" && " · bloco"}
                  </span>
                </td>
                <td className="p-2 align-middle">
                  <b className="block truncate max-w-[180px]" title={quemEntregou(l)}>{quemEntregou(l)}</b>
                  <span className="block mt-0.5"><ChipOrigem origem={l.origem} /></span>
                </td>
                <td className="p-2 align-middle"><BarraClassificacao linha={l} /></td>
                <td className="p-2 align-middle text-right font-mono tabular-nums">
                  {fmtKgBR(l.kgBase)}
                  {/* O ajuste da conferência aparece na linha, em vermelho: é a
                      diferença entre o que o produtor emitiu e o que desceu do
                      caminhão, e é o número que gera a conversa. */}
                  {l.ajusteKg != null && (
                    <span className="block text-[11px] text-destructive">
                      {l.ajusteKg > 0 ? "+" : "−"}{fmtKgBR(Math.abs(l.ajusteKg))} na conf.
                    </span>
                  )}
                </td>
                <td className="p-2 align-middle text-right font-mono tabular-nums font-semibold">{fmtMoedaBR(l.valor)}</td>
                <td className="p-2 align-middle text-xs"><Vencimento linha={l} /></td>
                <td className="p-2 align-middle"><ChipConferencia estado={l.conferencia} /></td>
                <td className="p-2 align-middle"><ChipCargaDaLinha carga={l.carga} /></td>
                <td className="p-2 align-middle"><ChipPagamento status={l.pagamento} /></td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-muted/30 text-xs">
            <tr className="border-t">
              <td className="p-2" />
              <td className="p-2" colSpan={3}>
                {totais.n} {totais.n === 1 ? "romaneio" : "romaneios"} · {totais.nProdutores} {totais.nProdutores === 1 ? "produtor" : "produtores"}
              </td>
              <td className="p-2 text-right font-mono tabular-nums" data-testid="total-kg">{fmtKgBR(totais.kg)}</td>
              <td className="p-2 text-right font-mono tabular-nums font-semibold" data-testid="total-valor">{fmtMoedaBR(totais.valor)}</td>
              <td className="p-2" colSpan={4} />
            </tr>
          </tfoot>
        </table>
      </div>
    </>
  );
}

// ─── A barra de seleção ──────────────────────────────────────────────────────

/**
 * Da lista para a carga, sem sair da lista.
 *
 * É o caminho mais curto do papel para o caminhão: ele filtra por "Sem carga",
 * marca os que vão juntos e manda. Fazer isso pela tela de Cargas custaria
 * abrir a carga, procurar cada romaneio numa segunda lista e marcá-los de novo.
 */
function BarraSelecao({ linhas, onLimpar }: { linhas: LinhaRomaneioDTO[]; onLimpar: () => void }) {
  const { toast } = useToast();
  const { abrir } = useNavegacao();
  const { data: cargas } = useCargasEmFormacao(true);
  const incluir = useIncluirEmCarga();
  // FORA da URL: é UI do momento, e um diálogo que sobrevive ao reload é um
  // diálogo que reabre sozinho depois de o corretor já ter desistido dele.
  const [montando, setMontando] = useState(false);
  const refs = linhas.map(l => l.ref);
  const kg = linhas.reduce((a, l) => a + l.kgBase, 0);

  function adicionar(cargaId: string, identificador: string) {
    incluir.mutate({ refs, cargaId }, {
      onSuccess: (r) => {
        onLimpar();
        toast({
          title: `${r.incluidos} ${r.incluidos === 1 ? "romaneio incluído" : "romaneios incluídos"} na ${identificador}`,
        });
      },
      // Nunca engolir: sem isto ele toca em Adicionar e nada acontece.
      onError: (e: Error) => toast({
        title: "Não consegui incluir na carga",
        description: e.message,
        variant: "destructive",
      }),
    });
  }

  const emFormacao = cargas ?? [];

  return (
    <div
      className="sticky bottom-16 md:bottom-4 z-20 rounded-lg border bg-card shadow-lg p-3 flex flex-wrap items-center gap-2"
      data-testid="barra-selecao"
    >
      <span className="text-sm">
        <b>{linhas.length}</b> {linhas.length === 1 ? "romaneio selecionado" : "romaneios selecionados"}
        {" · "}<b className="tabular-nums">{fmtKgBR(kg)}</b>
      </span>
      <div className="flex flex-wrap gap-2 ml-auto">
        <Button variant="ghost" size="sm" onClick={onLimpar} data-testid="button-limpar-selecao">Limpar</Button>

        {/* Até duas cargas viram botão direto; mais que isso viraria uma fileira
            de botões que não cabe no celular — aí o caminho é o formulário. */}
        {emFormacao.slice(0, MAX_CARGAS_NA_BARRA).map(c => (
          <Button
            key={c.id}
            variant="outline"
            size="sm"
            disabled={incluir.isPending}
            onClick={() => adicionar(c.id, c.identificador)}
            data-testid={`button-adicionar-${c.identificador}`}
          >
            Adicionar à {c.identificador}
          </Button>
        ))}

        <Button size="sm" onClick={() => setMontando(true)} data-testid="button-montar-nova-carga">
          <Truck className="h-4 w-4 mr-1.5" />Montar nova carga
        </Button>
      </div>

      <DialogNovaCargaMinima
        aberta={montando}
        onFechar={() => setMontando(false)}
        refs={refs}
        kg={kg}
        onCriada={() => { setMontando(false); onLimpar(); }}
      />
    </div>
  );
}

/**
 * "Monte um caminhão com estes" — o mínimo para a carga nascer.
 *
 * ⚠️ PROVISÓRIO, e de propósito: o "Nova carga" de verdade (cliente, capacidade
 * em chips, lista de romaneios marcáveis) é a tela do PR B. Aqui ficam os três
 * campos sem os quais a carga não serve para nada — variedade, carregamento e
 * capacidade —, para que a seleção múltipla já funcione de ponta a ponta neste
 * PR em vez de levar a um botão morto.
 *
 * ⚠️ SEM PREÇO DE VENDA. A carga é o controle do corretor nesta fase; dados de
 * venda ficam fora da UI (decisão D2), e a coluna continua no banco.
 */
function DialogNovaCargaMinima({
  aberta, onFechar, refs, kg, onCriada,
}: {
  aberta: boolean;
  onFechar: () => void;
  refs: string[];
  kg: number;
  onCriada: () => void;
}) {
  const { toast } = useToast();
  const { abrir } = useNavegacao();
  const criar = useCargaComRomaneios();
  const [variedade, setVariedade] = useState("MISTA");
  const [data, setData] = useState("");
  const [alvo, setAlvo] = useState(String(CAPACIDADE_REFERENCIA_KG));

  function salvar() {
    criar.mutate(
      {
        refs,
        carga: {
          // NULL = carga MISTA, não "não informado": Prata e Nanica no mesmo
          // caminhão é rotina.
          variedade: variedade === "MISTA" ? null : variedade,
          dataCarregamento: data || null,
          alvoKg: alvo ? parseInt(alvo, 10) : null,
        },
      },
      {
        onSuccess: (r) => {
          onCriada();
          toast({ title: `${r.identificador} criada com ${r.incluidos} ${r.incluidos === 1 ? "romaneio" : "romaneios"}` });
          abrir(`/mercado/cargas/${r.cargaId}`);
        },
        onError: (e: Error) => toast({
          title: "Não consegui criar a carga",
          description: e.message,
          variant: "destructive",
        }),
      },
    );
  }

  const faltam = alvo ? parseInt(alvo, 10) - kg : null;

  return (
    <Dialog open={aberta} onOpenChange={o => !o && onFechar()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Montar nova carga</DialogTitle></DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {refs.length} {refs.length === 1 ? "romaneio" : "romaneios"} · <b className="tabular-nums">{fmtKgBR(kg)}</b>
          </p>

          <div className="space-y-2">
            <Label>Variedade</Label>
            <Segmented<string>
              valor={variedade}
              onChange={setVariedade}
              testId="seg-variedade-carga"
              className="[&>button]:flex-1"
              opcoes={[
                { id: "PRATA", label: "Prata" },
                { id: "NANICA", label: "Nanica" },
                { id: "MISTA", label: "Mista" },
              ]}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="carga-data">Carregamento</Label>
            <Input id="carga-data" type="date" value={data} onChange={e => setData(e.target.value)} data-testid="input-carregamento" />
          </div>

          <div className="space-y-2">
            <Label>Capacidade do caminhão</Label>
            <div className="flex gap-2 flex-wrap">
              {CAPACIDADES_KG.map(c => (
                <Button
                  key={c} type="button" size="sm"
                  variant={alvo === String(c) ? "default" : "outline"}
                  onClick={() => setAlvo(String(c))}
                  data-testid={`chip-capacidade-${c}`}
                >
                  {c / 1000} t
                </Button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              Serve para mostrar quanto falta. Pode mudar depois.
              {faltam != null && faltam > 0 && <> Com os selecionados, faltam <b className="tabular-nums">{fmtKgBR(faltam)}</b>.</>}
              {faltam != null && faltam < 0 && <> Os selecionados passam <b className="tabular-nums">{fmtKgBR(-faltam)}</b> da capacidade.</>}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onFechar}>Cancelar</Button>
          <Button onClick={salvar} disabled={criar.isPending} data-testid="button-criar-carga">
            {criar.isPending ? "Criando…" : "Criar carga"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.substring(1).toLowerCase();
}
